import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import Spinner from '../../components/Spinner'
import { toast } from '../../components/Toast'

type JobRecommendation = {
  strong: { jobs: string[]; reason: string }
  good: { jobs: string[]; reason: string }
  challenge: { jobs: string[]; reason: string }
  basis: string
}

export default function JobRecommend() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [data, setData] = useState<JobRecommendation | null>(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const fetchData = async (refresh = false) => {
    if (refresh) setRefreshing(true)
    else setLoading(true)
    try {
      const r = await client.get('/features/job-recommendation', { params: refresh ? { refresh: true } : {} })
      setData(r.data)
      if (refresh) toast('직업 추천이 재생성되었습니다.', 'success')
    } catch {
      toast('직업 추천을 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    fetchData()
  }, [isAuthenticated])

  const TIERS = data ? [
    { icon: '🔥', label: '강력 추천', bg: 'bg-red-900/20', border: 'border-red-500/30', text: 'text-red-400', jobs: data.strong.jobs, reason: data.strong.reason },
    { icon: '👍', label: '잘 맞아요', bg: 'bg-[var(--color-accent)]/10', border: 'border-[var(--color-accent)]/30', text: 'text-[var(--color-accent)]', jobs: data.good.jobs, reason: data.good.reason },
    { icon: '🤔', label: '도전해볼만 해요', bg: 'bg-[var(--color-surface)]', border: 'border-[var(--color-border-default)]', text: 'text-[var(--color-text-tertiary)]', jobs: data.challenge.jobs, reason: data.challenge.reason },
  ] : []

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/dashboard')} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">💼 직업 추천</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">내 팔자에 맞는 직업은?</p>
          </div>
          {data && (
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-accent)] disabled:opacity-40 transition-colors px-2 py-1 rounded-lg border border-[var(--color-border-default)] hover:border-[var(--color-border-accent)]"
            >
              {refreshing ? '럭키가 다시 고르는 중' : '🔄 재생성'}
            </button>
          )}
        </div>

        {loading || refreshing ? (
          <div className="flex flex-col items-center py-20 gap-3">
            <Spinner size="lg" />
            {refreshing && <p className="text-[var(--color-text-tertiary)] text-sm">AI가 사주를 분석하고 있습니다...</p>}
          </div>
        ) : data ? (
          <div className="space-y-4">
            {/* 분석 기반 */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl p-4">
              <p className="text-[var(--color-text-tertiary)] text-xs mb-1">분석 기반</p>
              <p className="text-[var(--color-text-secondary)] text-sm">{data.basis}</p>
            </div>

            <div className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-xl px-4 py-2.5 text-center">
              <p className="text-[var(--color-accent)] text-sm font-bold">🏪 편의점 추천 직업</p>
            </div>

            {TIERS.map((tier) => (
              <div key={tier.label} className={`${tier.bg} border ${tier.border} rounded-2xl p-5`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{tier.icon}</span>
                  <span className={`text-sm font-bold ${tier.text}`}>{tier.label}</span>
                </div>
                <p className="text-[var(--color-text-primary)] font-semibold mb-2">{tier.jobs.join(' / ')}</p>
                <p className="text-[var(--color-text-tertiary)] text-sm leading-relaxed">{tier.reason}</p>
              </div>
            ))}

            <button onClick={() => navigate('/counseling', { state: { autoStart: true, category: 'career', mode: 'chat' } })}
              className="w-full py-3 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity">
              💬 직업 더 자세히 상담하기
            </button>
          </div>
        ) : (
          <div className="text-center text-[var(--color-text-tertiary)] py-20">
            <p className="text-4xl mb-4">💼</p>
            <p>직업 추천을 불러올 수 없습니다.</p>
            <p className="text-[var(--color-text-disabled)] text-xs mt-2">프로필에서 MBTI를 입력하면 더 정확해요.</p>
          </div>
        )}
      </div>
    </div>
  )
}
